import {Palette} from 'hubbl-shared';
import {memo, useCallback, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import styled, {keyframes} from 'styled-components';
import {useAppSelector, useI18n} from '../../hooks';
import {RootRoute} from '../../routes/RootRoute';
import {UserSelector} from '../../store/user/UserSelector';
import {TextHeading5} from '../text/TextHeading5';
import {TextBodyMedium} from '../text/TextBodyMedium';
import {TextHeading7} from '../text/TextHeading7';
import {TextHeading6} from '../text/TextHeading6';
import {TextHeading8} from '../text/TextHeading8';
import {PrimaryButton} from '../buttons/primary-button/PrimaryButton';

const BANNER_DISMISSED_KEY = 'profileCompletionBannerDismissed';
const MIN_INTERESTS = 5;

export const ProfileCompletionBanner = memo(() => {
  const user = useAppSelector(UserSelector.getUserDetails);
  const navigate = useNavigate();
  const t = useI18n();

  const [isDismissed, setIsDismissed] = useState(() => {
    if (!user) return true;
    const dismissedData = localStorage.getItem(BANNER_DISMISSED_KEY);
    if (!dismissedData) return false;
    try {
      const parsed = JSON.parse(dismissedData);
      return parsed.userId === user.id;
    } catch {
      return false;
    }
  });

  const isProfileIncomplete = useMemo(() => {
    if (!user) return false;
    const hasPhoto = !!user.photoUrl;
    const hasInterests =
      !!user.interests && user.interests.length >= MIN_INTERESTS;
    return !hasPhoto || !hasInterests;
  }, [user]);

  const incompleteItems = useMemo(() => {
    if (!user) return [];
    const items: string[] = [];
    if (!user.photoUrl) items.push(t.profileCompletion.photo);
    if (!user.interests || user.interests.length < MIN_INTERESTS)
      items.push(t.profileCompletion.interests);
    return items;
  }, [user, t]);

  const handleDismiss = useCallback(() => {
    if (user) {
      localStorage.setItem(
        BANNER_DISMISSED_KEY,
        JSON.stringify({userId: user.id, dismissedAt: Date.now()}),
      );
    }
    setIsDismissed(true);
  }, [user]);

  const handleCompleteClick = useCallback(() => {
    if (!user) return;

    // Navigate to onboarding screens for incomplete items
    if (!user.interests || user.interests.length < MIN_INTERESTS) {
      navigate(RootRoute.OnboardingInterests, {
        state: {isReturningUser: true},
      });
    } else if (!user.photoUrl) {
      navigate(RootRoute.OnboardingAddPhoto);
    }
  }, [user, navigate]);

  // Don't render if dismissed, profile complete, or no user
  if (isDismissed || !isProfileIncomplete || !user) {
    return null;
  }

  return (
    <BannerWrapper>
      <BannerContent>
        <IconSection>✨</IconSection>
        <TextSection>
          <TextHeading5 text={t.profileCompletion.bannerTitle} />
          <TextHeading8
            color={Palette.almostBlack500}
            text={t.profileCompletion.bannerMessage.replace(
              '{items}',
              incompleteItems.join(` ${t.common.and} `),
            )}
          />
        </TextSection>
        <ActionsSection>
          <PrimaryButton
            isCompact
            label={t.profileCompletion.completeProfile}
            onClick={handleCompleteClick}
          />
          <DismissButton onClick={handleDismiss} aria-label="Dismiss">
            ✕
          </DismissButton>
        </ActionsSection>
      </BannerContent>
    </BannerWrapper>
  );
});

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const BannerWrapper = styled.div`
  background: linear-gradient(
    135deg,
    ${Palette.primaryPadua}20 0%,
    ${Palette.primaryCherokee}60 100%
  );
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 0px;
  margin-top: 16px;
  animation: ${slideIn} 0.3s ease-out;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
`;

const BannerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 600px) {
    flex-wrap: wrap;
  }
`;

const IconSection = styled.div`
  font-size: 24px;
  flex-shrink: 0;
`;

const TextSection = styled.div`
  flex: 1;
  min-width: 0;
`;

const ActionsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: 600px) {
    width: 100%;
    justify-content: flex-end;
    margin-top: 8px;
  }
`;

const CompleteButton = styled.button`
  background: ${Palette.primaryPadua};
  color: ${Palette.white};
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${Palette.primaryPadua};
    transform: translateY(-1px);
    filter: brightness(0.95);
  }
`;

const DismissButton = styled.button`
  background: transparent;
  border: none;
  color: ${Palette.almostBlack500};
  cursor: pointer;
  padding: 4px 8px;
  font-size: 16px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: ${Palette.almostBlackTransparent5};
    color: ${Palette.almostBlack};
  }
`;
